import { apiClient } from './apiClient';

export type TransactionOwner = 'gaurav' | 'sourabh' | 'company';

export type Transaction = {
  id: number;
  month_id: number;
  date: string;
  credit_amount: string;
  debit_amount: string;
  type: string | null;
  source: string | null;
  owner: TransactionOwner;
  remarks: string | null;
  created_at: string;
  updated_at: string;
};

export type TransactionCreatePayload = {
  date: string;
  /**
   * Accounting month in YYYY-MM (11→10 window). Optional; if omitted, backend derives from `date`.
   */
  month?: string;
  credit_amount: number | string;
  debit_amount: number | string;
  type?: string;
  source?: string;
  owner: TransactionOwner;
  remarks?: string;
};

type ListParams = {
  month?: string;
  owner?: TransactionOwner;
};

type PaginatedResponse<T> = {
  data: T[];
};

export async function listTransactions(token: string, params: ListParams) {
  const query = new URLSearchParams();
  if (params.month) {
    query.set('month', params.month);
  }
  if (params.owner) {
    query.set('owner', params.owner);
  }

  const path = `/transactions${query.toString() ? `?${query.toString()}` : ''}`;
  return apiClient<PaginatedResponse<Transaction>>(path, {
    method: 'GET',
    token,
  });
}

export async function createTransaction(token: string, payload: TransactionCreatePayload) {
  return apiClient<{ data: Transaction }>('/transactions', {
    method: 'POST',
    token,
    body: JSON.stringify(payload),
  });
}

export async function updateTransaction(token: string, id: number, payload: Partial<TransactionCreatePayload>) {
  return apiClient<{ data: Transaction }>(`/transactions/${id}`, {
    method: 'PUT',
    token,
    body: JSON.stringify(payload),
  });
}

export async function deleteTransaction(token: string, id: number) {
  return apiClient<{ message: string }>(`/transactions/${id}`, {
    method: 'DELETE',
    token,
  });
}

export async function transactionsSummary(token: string) {
  return apiClient<{ data: Record<string, { credit: number; debit: number; net: number }> }>(`/transactions/summary`, {
    method: 'GET',
    token,
  });
}

export type OverallBalance = {
  opening_balance: number;
  total_earnings: number;
  total_expenses: number;
  total_profit_allocations: number;
  total_owner_draws?: number;
  overall_balance: number;
};

export async function transactionsOverallBalance(token: string) {
  return apiClient<{ data: Record<TransactionOwner, OverallBalance> }>(`/transactions/overall-balance`, {
    method: 'GET',
    token,
  });
}
